<template>
  <div>
    <!-- blocked candidate (v2): shown immediately, and the parent form's send stays disabled
         (overallStatus is false while blocked) -->
    <ApplicantBlockBanner v-if="offer && offer.applicant" :applicant="offer.applicant" @released="onBlockReleased" />
    <div v-if="isLoading">
      <button class="button is-large is-fullwidth is-loading"></button>
    </div>
    <div class="mb-6" v-else>
      <div style="display:none">{{ areAllOffersMarked }}</div>

      <div v-if="'warningContractSign' in warnings">
        <label class="checkbox">
          <BaseCheckbox class="ml-4" v-model="warnings.warningContractSign" />
          <span class="has-text-danger is-size-4">לחברה לא שמור חוזה חתום במערכת</span>
        </label>
      </div>

      <div class="mt-2" v-if="'warningCompanyEmployee' in warnings">
        <label class="checkbox">
          <BaseCheckbox class="ml-4" v-model="warnings.warningCompanyEmployee" />
          <span class="has-text-danger has-text-weight-bold">חשוב מאוד! להתקשר לחברה ולרשום אותם למערכת אחרי ההגשה כי אין להם משתמש</span>
        </label>
      </div>

      <div class="mt-2" v-if="'warningHistoryCompany' in warnings">
        <label class="checkbox">
          <BaseCheckbox class="ml-4" v-model="warnings.warningHistoryCompany" />
          <span class="has-text-danger has-text-weight-bold">
            אזהרה: המועמד/ת {{ workHistoryStatusText }} בחברה זו
          </span>
        </label>
        <div v-if="matchingWorkHistory.length > 0" class="mr-8 mt-1" style="font-size: 0.9em; color: #555;">
          <div v-for="history in matchingWorkHistory" :key="history.id" style="margin-top: 5px;">
            <span class="has-text-weight-semibold">{{ history.job_title || 'תפקיד לא צוין' }}</span>
            <span v-if="history.still_working" class="has-text-danger has-text-weight-bold"> (עובד/ת כרגע!)</span>
            <span v-else-if="history.year_start || history.year_end" style="color: #999;">
              ({{ formatWorkPeriod(history) }})
            </span>
            <span v-if="history.department" style="color: #666;"> - {{ history.department }}</span>
          </div>
        </div>
      </div>

      <div class="mt-2" v-if="'warningCurrentCompany' in warnings">
        <label class="checkbox">
          <BaseCheckbox class="ml-4" v-model="warnings.warningCurrentCompany" />
          <span class="has-text-danger has-text-weight-bold">
            אזהרה: המועמד עובד בחברת {{ offer.applicant.current_company }}
          </span>
        </label>
      </div>

      <div class="mt-2" v-if="'warningNoGoCompanies' in warnings">
        <label class="checkbox">
          <BaseCheckbox class="ml-4" v-model="warnings.warningNoGoCompanies" />
          <span class="has-text-danger has-text-weight-bold">
            אזהרה: המועמד לא מאשר הגשה לחברות {{ offer.applicant.no_go_companies }}
          </span>
        </label>
      </div>

      <div class="mt-2" v-if="'warningCompany' in warnings">
        <label class="checkbox">
          <BaseCheckbox class="ml-4" v-model="warnings.warningCompany" />
          <span class="has-text-danger has-text-weight-bold">Company priority warning: {{ priorityWarningCompany }}</span>
        </label>
      </div>

      <div class="mt-2" v-if="'warningPosition' in warnings">
        <label class="checkbox">
          <BaseCheckbox class="ml-4" v-model="warnings.warningPosition" />
          <span class="has-text-danger has-text-weight-bold">Position priority warning: {{ priorityWarningPosition }}</span>
        </label>
      </div>

      <div class="mt-2" v-if="'warningCompanyExposedLately' in warnings">
        <label class="checkbox">
          <BaseCheckbox class="ml-4" v-model="warnings.warningCompanyExposedLately" />
          <span class="has-text-danger has-text-weight-bold">No exposures in the last 3 months</span>
        </label>
      </div>

      <div class="mt-2" v-if="redOffers.length > 0">
        <p><b>הצעות עבור המועמד כבר נשלחו בעבר:</b></p>
        <div class="red_container">
          <CvColorAlertMessage
          v-for="(offer, index) in redOffers"
          :offer="offer"
          :key="offer.id"
          :class="{ color_message_border: index != redOffers.length - 1 }"
          @updateMark="handleOfferMark"
          :mark-it="validation"
          />
        </div>
      </div>

      <div class="mt-2" v-if="greenOffers.length > 0">
        <p><b>הצעות שהוגשו על ידי המועמד לאותו המעסיק וממתינות לתגובה:</b></p>
        <div class="green_container">
          <CvColorAlertMessage
          v-for="(offer, index) in greenOffers"
          :offer="offer"
          :key="offer.id"
          :class="{ color_message_border: index != greenOffers.length - 1 }"
          @updateMark="handleOfferMark"
          :mark-it="validation"
          />
        </div>
      </div>

      <div v-if="offer.position.status == 'editing'">
        <p class="has-text-danger"><b>POSITION STATUS IS EDITING</b></p>
      </div>

    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import CvColorAlertMessage from '@/components/modals/CVColorAlertMessage'
import BaseCheckbox from '@/components/shared/atoms/BaseCheckbox.vue'
import ApplicantBlockBanner from '@/components/ApplicantBlockBanner.vue'
import { isApplicantBlocked, markReleased } from '@/custom/helpers/applicant_block.js'

export default {
  name: 'CvOfferAlerts',
  components: {
    CvColorAlertMessage,
    BaseCheckbox,
    ApplicantBlockBanner
  },
  props: {
    offer: {
      type: Object
    },
    validation: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isLoading: true,
      historyCompanies: [],
      greenOffers: [],
      redOffers: [],
      markedOfferStatus: {},
      companyEmployees: [],
      companyExposedLately: false,
      warnings: {}
    }
  },
  computed: {
    ...mapGetters('offerStatuses', ['getRedListNames', 'getDarkRedListNames', 'getGreenListNames', 'getWarningsRedList']),
    historyCompanyWarning () {
      if (!this.historyCompanies || this.historyCompanies.length === 0) return false
      return this.matchingWorkHistory.length > 0
    },
    matchingWorkHistory () {
      if (!this.historyCompanies || !this.offer.position.company.history_company_id) return []
      return this.historyCompanies.filter(historyCompany =>
        historyCompany.history_company &&
        historyCompany.history_company.id == this.offer.position.company.history_company_id
      )
    },
    workHistoryStatusText () {
      const hasCurrentWork = this.matchingWorkHistory.some(h => h.still_working)
      return hasCurrentWork ? 'עובד/ת כרגע' : 'עבד/ה'
    },
    priorityWarningCompany () {
      return this.offer.position.company.priority_warning
    },
    priorityWarningPosition () {
      return this.offer.position.priority_warning
    },
    companyHaveEmployee () {
      return this.companyEmployees && this.companyEmployees.length > 0
    },
    isWarningsChecked () {
      return Object.values(this.warnings).every(Boolean)
    },
    areAllOffersMarked () {
      const statusValues = Object.values(this.markedOfferStatus)
      if (statusValues.length === 0) return true
      return statusValues.every(value => value === true)
    },
    isBlocked () {
      return isApplicantBlocked(this.offer && this.offer.applicant)
    },
    overallStatus () {
      // A blocked candidate can never be "all clear": parents disable their send buttons on false.
      return !this.isBlocked && this.isWarningsChecked && this.areAllOffersMarked
    }
  },
  watch: {
    overallStatus: {
      handler (newStatus) {
        this.$emit('warningsStatus', newStatus)
      },
      immediate: true
    }
  },
  created () {
    this.init()
  },
  methods: {
    onBlockReleased () {
      markReleased(this, this.offer && this.offer.applicant)
    },
    async init () {
      try {
        this.isLoading = true

        // Prepare all requests
        const [historyCompaniesResponse, offersResponse, companyEmployeesResponse, companyExposedLately] = await Promise.all([
          this.$network_utils.getApplicantHistoryCompanies({ current_applicant_id: this.offer.applicant.resource_id }),
          this.$network_utils.getResourceOffers({
            resource_type: 'Applicant',
            company_id: this.offer.position.company.resource_id,
            resource_id: this.offer.applicant.resource_id,
            statuses: [ ...this.getGreenListNames, ...this.getRedListNames, ...this.getDarkRedListNames, 'company_never_replied' ].join(',')
          }),
          this.$network_utils.getCompanyEmployees({ company_id: this.offer.position.company.resource_id }),
          this.$network_utils.getCompanyExposedLately({ id: this.offer.position.company.resource_id })
        ])

        // Set data from responses
        this.historyCompanies = historyCompaniesResponse
        this.companyEmployees = companyEmployeesResponse.data || companyEmployeesResponse
        this.companyExposedLately = companyExposedLately.exposed

        // Process offers
        this.processOffers(offersResponse)

        // Initialize checkbox states
        this.initializeCheckboxStates()
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        this.isLoading = false
      }
    },
    initializeCheckboxStates () {
      this.warnings = {
        ...(!this.offer.isContractSign && { warningContractSign: false }),
        ...(this.priorityWarningCompany && { warningCompany: false }),
        ...(this.priorityWarningPosition && { warningPosition: false }),
        ...(this.historyCompanyWarning && { warningHistoryCompany: false }),
        ...(!this.companyHaveEmployee && { warningCompanyEmployee: false }),
        ...(this.companyExposedLately && { warningCompanyExposedLately: false }),
        ...(this.offer.applicant.current_company && { warningCurrentCompany: false }),
        ...(this.offer.applicant.no_go_companies && { warningNoGoCompanies: false })
      }
    },
    processOffers (offers) {
      this.greenOffers = offers.filter(offer => {
        if (offer.status.name == 'company_never_replied') return true
        if (offer.status.name == 'awaiting_company') return false
        return this.getGreenListNames.includes(offer.status.name)
      })

      this.redOffers = offers.filter(offer => {
        return this.getWarningsRedList.map(s => s.name).includes(offer.status.name)
      }).sort((a, b) => {
        return (this.getWarningOrder(a.status.name) - this.getWarningOrder(b.status.name))
      })

      this.markedOfferStatus = {}
      const allOffers = [...this.greenOffers, ...this.redOffers]
      allOffers.forEach(offer => { this.$set(this.markedOfferStatus, offer.id, false) })

      if (this.redOffers.length) {
        this.$emit('setRedAlert', this.redOffers[0])
      }
    },
    getWarningOrder (status) {
      const orderMap = {
        'company_familiar': 0,
        'company_rejected': 1,
        'admin_rejected': 4
      }
      return orderMap[status] || 3
    },
    handleOfferMark (id, isMarked) {
      if (id !== -1) {
        this.$set(this.markedOfferStatus, id, isMarked)
      }
    },
    formatWorkPeriod (history) {
      const startYear = history.year_start || '?'
      const startMonth = history.month_start ? `/${history.month_start}` : ''
      const endYear = history.year_end || 'הווה'
      const endMonth = history.month_end && history.year_end ? `/${history.month_end}` : ''
      return `${startYear}${startMonth} - ${endYear}${endMonth}`
    }
  }
}
</script>
<style scoped>
  .red_container {
    border: 1px solid red;
    border-radius: 3px;
    padding: 10px;
  }
  .green_container {
    border: 1px solid green;
    border-radius: 3px;
    padding: 10px;
  }
  .color_message_border {
    margin-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding-bottom: 10px;
  }
</style>
